import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Mail, Phone, MapPin, Linkedin, Github, ExternalLink, Twitter } from 'lucide-react';

const Contact = () => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, threshold: 0.3 });

    const contactInfo = [
        {
            icon: Mail,
            label: 'Email',
            value: 'mr.samim.samim@gmail.com',
            href: 'mailto:mr.samim.samim@gmail.com'
        },
        {
            icon: Phone,
            label: 'Phone',
            value: '+91 9053329711',
            href: 'tel:+919053329711'
        },
        {
            icon: MapPin,
            label: 'Location',
            value: 'Gurugram, India',
            href: '#'
        }
    ];

    const socialLinks = [
        {
            icon: Linkedin,
            label: 'LinkedIn',
            href: 'https://www.linkedin.com/in/mr-samim',
            color: 'hover:text-blue-600'
        },
        {
            icon: Github,
            label: 'GitHub',
            href: 'https://github.com/samim',
            color: 'hover:text-gray-900 dark:hover:text-gray-100'
        },
        {
            icon: Twitter,
            label: 'Twitter',
            href: 'https://x.com/MrSamim1218742',
            color: 'hover:text-primary'
        },
        {
            icon: ExternalLink,
            label: 'Portfolio',
            href: '#home',
            color: 'hover:text-primary'
        }
    ];

    return (
        <section id="contact" className="py-20">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 50 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="text-center mb-16">
                        <motion.h2
                            className="text-3xl sm:text-4xl font-bold text-foreground mb-4"
                            initial={{ opacity: 0, y: 20 }}
                            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            Get In Touch
                        </motion.h2>
                        <motion.div
                            className="w-20 h-1 bg-accent mx-auto"
                            initial={{ scaleX: 0 }}
                            animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                        />
                        <motion.p
                            className="text-foreground/70 mt-6 max-w-2xl mx-auto"
                            initial={{ opacity: 0, y: 20 }}
                            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            I'm always open to discussing new opportunities, creative projects, or just having a chat about Flutter development.
                        </motion.p>
                    </div>

                    <div className="max-w-6xl mx-auto">
                        {/* Contact Information */}
                        <motion.div
                            className="space-y-12"
                            initial={{ opacity: 0, x: -50 }}
                            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            <div>
                                <h3 className="text-2xl font-semibold text-foreground mb-8 text-center">
                                    Contact Information
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                    {contactInfo.map((item, index) => {
                                        const IconComponent = item.icon;
                                        const isActionable = item.href !== '#';

                                        return (
                                            <motion.div
                                                key={item.label}
                                                className={`flex flex-row items-center justify-center gap-4 group ${isActionable ? 'cursor-pointer' : ''}`}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                                transition={{
                                                    opacity: { duration: 0.6, delay: 0.5 + index * 0.1 },
                                                    y: { duration: 0.6, delay: 0.5 + index * 0.1 },
                                                    scale: { duration: 0.2 }
                                                }}
                                                whileHover={isActionable ? { scale: 1.05 } : {}}
                                                whileTap={isActionable ? { scale: 0.95 } : {}}
                                                onClick={() => isActionable && window.open(item.href, '_blank')}
                                            >
                                                <div className={`w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center transition-all duration-200 ${isActionable ? 'group-hover:bg-primary group-hover:text-white' : ''}`}>
                                                    <IconComponent className={`w-5 h-5 text-accent ${isActionable ? 'group-hover:text-white' : ''}`} />
                                                </div>
                                                <div className="flex flex-row items-center gap-2">
                                                    <p className="text-sm font-medium text-foreground/60">
                                                        {item.label}
                                                    </p>
                                                    <p className={`text-foreground font-medium transition-colors duration-200 ${isActionable ? 'group-hover:text-primary' : ''}`}>
                                                        {item.value}
                                                    </p>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Social Links */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                transition={{ duration: 0.6, delay: 0.8 }}
                                className="flex flex-col items-center"
                            >
                                <h4 className="text-lg font-semibold text-foreground mb-4">
                                    Follow Me
                                </h4>
                                <div className="flex gap-4">
                                    {socialLinks.map((social, index) => {
                                        const IconComponent = social.icon;
                                        return (
                                            <motion.button
                                                key={social.label}
                                                className={`w-12 h-12 bg-background border border-border rounded-lg flex items-center justify-center text-foreground/60 hover:border-accent transition-all duration-200 ${social.color}`}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={() => {
                                                    if (social.href.startsWith('#')) {
                                                        document.querySelector(social.href).scrollIntoView({ behavior: 'smooth' });
                                                    } else {
                                                        window.open(social.href, '_blank');
                                                    }
                                                }}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                                transition={{
                                                    opacity: { duration: 0.4, delay: 0.9 + index * 0.1 },
                                                    y: { duration: 0.4, delay: 0.9 + index * 0.1 },
                                                    scale: { duration: 0.2 }
                                                }}
                                            >
                                                <IconComponent className="w-5 h-5" />
                                            </motion.button>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        </motion.div>

                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Contact;